import IconButton from '@/components/buttons/IconButton'
import LoadMoreButton, { useLoadMore } from '@/components/buttons/LoadMoreButton'
import { AppLayout } from '@/components/Layouts/AppLayout'
import MonthPicker, { formatAsQry, useMonthPicker } from '@/components/MonthPicker'
import { useLanguage, useLayout } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { Format } from '@/utils/Format'
import { svgs } from '@/utils/Images'
import { Entry } from '@models/Index'
import { appUtils } from 'lib/AppUtils'
import Link from 'next/link'
import { FC, Fragment, memo, useEffect } from 'react'
import useSWRImmutable from 'swr/immutable'

export default function Entries() {
  const {
    errors: { no_data },
  } = useLanguage()
  const { isDesktop } = useLayout()

  const { pageIndex, total, setTotal, list, setList, resetParams, loadMore } = useLoadMore()

  const { filter, setFilter, onApply, onReset } = useMonthPicker(resetParams)

  const { data, mutate, isValidating, isLoading } = useSWRImmutable(
    typeof isDesktop != 'undefined'
      ? `${API_URL.entries}?perPage=${isDesktop === false ? '6' : '12'}&page=${pageIndex + 1}${filter ? '&sort_by_month=' + formatAsQry(filter) : ''}`
      : null,
    appUtils.fetcher
  )

  useEffect(() => {
    if (!isValidating && !isLoading && data?.data?.length > 0) {
      setList(prev =>
        data?.data?.reduce?.((arr, item) => (arr?.some(({ competition_id }) => competition_id == item.competition_id) ? arr : arr.concat(item)), prev)
      )
      setTotal(data?.total)
    }
  }, [isValidating, isLoading, data])

  return (
    <AppLayout
      showBanner
      title='entry_list'
      titleOuterCls='sticky top-16 md:top-20 z-[51]' // z-index must be larger than LoadingView's
      titleInnerCls='flex items-center'
      filter={
        <Fragment>
          <MonthPicker valueCtrl={[filter, setFilter]} onApply={onApply} className='ml-auto' />
          <IconButton
            className='ml-2 h-8 w-8 p-1.5 md:ml-4'
            onClick={() =>
              onReset().then(() => {
                if (!isValidating && !isLoading) {
                  mutate()
                }
              })
            }
          >
            {svgs.reload}
          </IconButton>
        </Fragment>
      }
      outerCls='relative'
      innerCls='grid w-full animate-fade-in-down grid-cols-1 grid-rows-6 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-3 lg:grid-rows-4 lg:gap-6'
    >
      {list?.length > 0 ? list?.map((item: Entry, index: number) => <Item key={'row-' + index} data={item} />) : null}

      {isValidating || isLoading ? (
        <Skeletons />
      ) : data?.data?.length > 0 ? null : (
        <div className='col-span-full row-span-full animate-fade-in-down place-self-center py-24 text-center'>{no_data}</div>
      )}

      <LoadMoreButton className='col-span-full h-10 justify-self-center' onClick={loadMore} current={list?.length} total={total} />
    </AppLayout>
  )
}

const Item: FC<{ data: Record<string, any> }> = ({ data }) => {
  const { entries: entryStr } = useLanguage()
  return (
    <div className='card-container relative col-span-1 flex animate-fade-in-down flex-col p-4 duration-1000'>
      <p className='font-semibold'>{data.title}</p>
      <p className='mt-2 text-sm text-stone-600'>{entryStr.draw_date + Format.dateHourMinuteFormat(data.draw_date)}</p>
      <p className='my-2 text-sm text-stone-600'>
        {entryStr.winning}: {data.winner_number?.length > 0 ? '#' + data.winner_number : entryStr.no_wining}
      </p>
      {data.is_google_drive ? (
        <a href={data.url} target='blank' className='mt-auto cursor-pointer text-sm text-cyan-v2 underline hover:text-amber-v1'>
          {entryStr.view_entries}
        </a>
      ) : (
        <Link href={'/entries/' + data.competition_id}>
          <span className='mt-auto cursor-pointer text-sm text-cyan-v2 underline hover:text-amber-v1'>{entryStr.view_entries}</span>
        </Link>
      )}
    </div>
  )
}

const Skeletons: FC<{ numOfItems?: number }> = memo(function Skeleton({ numOfItems = 12 }) {
  const skeletons = []
  for (let index = 0; index < numOfItems; index++) {
    skeletons.push(
      <div key={index} className='card-container animate-fade-in-down'>
        <div className='skeleton-wrap p-4'>
          <div className='skeleton-line h-4'></div>
          <div className='skeleton-line mt-4 h-3 w-1/2'></div>
          <div className='skeleton-line mt-4 h-3 w-1/2'></div>
          <div className='skeleton-line mt-8 h-3 w-1/2'></div>
        </div>
      </div>
    )
  }
  return <Fragment>{skeletons}</Fragment>
})
